import type { Faro, MetaUser } from "@grafana/faro-web-sdk";

export enum AppLoggerClientType {
  grafanaFaro,
}

// The Grafana Faro client. setUser is widened because AppLogger sends fields beyond Faro's
// MetaUser (userName, customerId, portalLink, featureFlags).
export interface IAppLoggerClient {
  api: Omit<Faro["api"], "setUser"> & {
    setUser: (user: MetaUser & Record<string, string | undefined>) => void;
  };
}

export interface IAppLogger {
  client: IAppLoggerClient;
  clientType: AppLoggerClientType;
}

export enum UserActions {
  aiAssistantNewContext = "ai-assistant-new-context", // by creating a new context in the AI Assistant
  aiAssistantSendMessage = "ai-assistant-send-message", // by sending a message in the AI Assistant
  aiAssistantReceiveResponse = "ai-assistant-receive-response", // by receiving a response in the AI Assistant
  aiAssistantPromptTemplatesView = "ai-assistant-prompt-templates-view", // by viewing the AI Assistant prompt templates
  aiAssistantPromptBookmarksView = "ai-assistant-prompt-bookmarks-view", // by viewing the AI Assistant prompt bookmarks
  aiAssistantPromptRecentHistoryView = "ai-assistant-prompt-recent-history-view", // by viewing the AI Assistant recent history
  aiAssistantPromptLibraryView = "ai-assistant-prompt-library-view", // by viewing the AI Assistant prompt library
  aiAssistantPromptLibraryCategoryChange = "ai-assistant-prompt-library-category-change", // by changing category in the AI Assistant prompt library
  aiAssistantPromptCustomTemplateCreate = "ai-assistant-prompt-custom-template-create", // by creating a custom template in the AI Assistant prompt library
  aiAssistantPromptCustomTemplateUpdate = "ai-assistant-prompt-custom-template-update", // by updating a custom template in the AI Assistant prompt library
  aiAssistantPromptCustomTemplateDelete = "ai-assistant-prompt-custom-template-delete", // by deleting a custom template in the AI Assistant prompt library
  aiAssistantPromptAgentWrapUpSummaryTemplateCreate = "ai-assistant-prompt-agent-wrap-up-summary-template-create", // by creating the agent wrap-up summary template in the AI Assistant prompt
  aiAssistantPromptAgentWrapUpSummaryTemplateUpdate = "ai-assistant-prompt-agent-wrap-up-summary-template-update", // by updating the agent wrap-up summary template in the AI Assistant prompt library
  aiAssistantPromptBookmarkCreate = "ai-assistant-prompt-bookmark-create", // by creating a prompt bookmark in the AI Assistant
  aiAssistantPromptBookmarkDelete = "ai-assistant-prompt-bookmark-delete", // by deleting a prompt bookmark in the AI Assistant
  exportCurrentPageToPdf = "export-current-page-to-pdf", // by exporting the current page to PDF
  exportAllPagesToPdf = "export-all-pages-to-pdf", // by exporting all pages to PDF
  exportTranscriptionToPdf = "export-transcription-to-pdf", // by exporting the interaction transcription to PDF
  exportInteractionsToCsv = "export-interactions-to-csv", // by exporting interactions to CSV
  filterDialogApply = "filter-dialog-apply", // by clicking the "Apply" button in the topbar filter dialog
  filterDialogCancel = "filter-dialog-cancel", // by clicking the "Cancel" button in the topbar filter dialog
  filterTopbarTimePeriodChange = "filter-topbar-time-period-change", // by changing the time period in the topbar filter
  filterTopbarChipsRemove = "filter-topbar-chips-remove", // by removing a chips item in the topbar filter
  filterTopbarReset = "filter-topbar-reset", // by clicking the "Reset" button in the topbar filter
  interactionPlayAudio = "interaction-play-audio", // by playing an interaction audio
  interactionsSearch = "interactions-search", // by searching interactions using the topbar search box
  interactionsFilterApplied = "interactions-filter-applied", // by applying filters in the interactions page
  sharedLinkCreate = "shared-link-create", // by creating a shared link
  scorecardCreate = "scorecard-create", // by creating a new scorecard
  scorecardEdit = "scorecard-edit", // by editing an existing scorecard
  scorecardApply = "scorecard-apply", // by applying a scorecard
  scorecardActivate = "scorecard-activate", // by activating a scorecard
  scorecardDeactivate = "scorecard-deactivate", // by deactivating a scorecard
  scorecardDuplicate = "scorecard-duplicate", // by duplicating a scorecard
  scorecardDelete = "scorecard-delete", // by deleting a scorecard
  scorecardDeleteResult = "scorecard-delete-result", // by deleting a scorecard result
  scorecardViewResult = "scorecard-view-result", // by viewing a scorecard result
  scorecardExportPdfResult = "scorecard-export-pdf-result", // by downloading a scorecard result as PDF
  scorecardExport = "scorecard-export", // by exporting a scorecard's definition as a file
  scorecardImport = "scorecard-import", // by importing a scorecard file as a new scorecard
  transcriptionPhraseUpdate = "transcription-phrase-update", // by editing a phrase in the interaction transcription
  scoringScheduleActivate = "scoring-schedule-activate", // by activating a scoring schedule
  scoringScheduleCreate = "scoring-schedule-create", // by creating a new scoring schedule
  scoringScheduleDeactivate = "scoring-schedule-deactivate", // by deactivating a scoring schedule
  scoringScheduleDelete = "scoring-schedule-delete", // by deleting a scoring schedule
  scoringScheduleDuplicate = "scoring-schedule-duplicate", // by duplicating a scoring schedule
  scoringScheduleEdit = "scoring-schedule-edit", // by editing an existing scoring schedule
  scoringSchedulePause = "scoring-schedule-pause", // by pausing a scoring schedule
  scoringScheduleRun = "scoring-schedule-run", // by running a scoring schedule
  viewFilterCreate = "view-filter-create", // by creating a new view filter
  viewFilterUpdate = "view-filter-update", // by updating an existing view filter
  viewFilterMove = "view-filter-move", // by moving a view filter
  viewFilterDelete = "view-filter-delete", // by deleting a view filter
  viewFolderCreate = "view-folder-create", // by creating a new view folder
  viewFolderUpdate = "view-folder-update", // by updating an existing view folder
  viewFolderDelete = "view-folder-delete", // by deleting a view folder
}
